import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import type { ILink, IPagePath } from "@contentful-api/types/contentful";
import type { TextProps } from "@ecco/ui/text";
import { Text } from "@ecco/ui/text";
import { defaultUnderlineStyle } from "@ecco/ui/css/common";
import { isEmpty } from "lodash-es";
import { forwardRef } from "react";
import { useAuthFormStore } from "@store/authFormStore";
import { ModalTypes } from "@lib/enums/ModalTypes";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { getModalRumActionName } from "@lib/monitoring/rumActionNames";

/**
 * Modals that support "back" navigation when user navigates to sub-pages.
 * When clicking a link from these modals, the current modal type is stored
 * so the user can return to it via the back button.
 */
const MODALS_WITH_BACK_NAVIGATION = new Set([
    ModalTypes.createAccount,
    ModalTypes.login,
    ModalTypes.upgradeAccount,
]);

interface IModalLinkProps extends TextProps {
    link: ILink | IPagePath;
}

export const ModalLink = forwardRef<HTMLParagraphElement, IModalLinkProps>(
    ({ link, children, ...rest }, ref) => {
        const _link = link as ILink;
        const label = _link?.fields?.label || "";
        const description = _link?.fields?.description || "";
        const descriptionRichText = _link?.fields?.descriptionRichText || "";
        const target = _link?.fields?.target || "";

        const { showModal, modalType } = useGlobalModalContext();
        const { get: getMicrocopy } = useMicrocopy();
        const setReturnModal = useAuthFormStore((state) => state.setReturnModal);

        const onClick = (event: React.MouseEvent<HTMLElement>) => {
            if (modalType && MODALS_WITH_BACK_NAVIGATION.has(modalType)) {
                setReturnModal(modalType);
            }
            showModal(_link?.fields?.modal, {
                description,
                label,
                descriptionRichText,
                target,
                ...(_link?.fields?.modal === ModalTypes.giftWrapping && {
                    microcopies: {
                        title:
                            getMicrocopy(CommonCMS.page, CommonCMS.giftWrapping.modalTitle) ?? "",
                    },
                }),
            });
            event.preventDefault();
        };

        return (
            <Text
                data-type="modal-link"
                as="button"
                // Typeless, it would be a form's default button: Enter in a field opens the modal.
                type="button"
                color="inherit"
                fontSize="inherit"
                cursor="pointer"
                onClick={onClick}
                data-testid={`${_link?.fields?.modal}-link`}
                data-dd-action-name={getModalRumActionName(_link?.fields?.modal)}
                {...rest}
                ref={ref}
                sx={{
                    // We need to spread the sx prop so it's not overwritten by the default styles
                    ...rest?.sx,
                    // We are not using Link here, so we need to overwrite the default styles of the Button with our link underline type styles
                    transitionProperty: "common",
                    transitionDuration: "fast",
                    transitionTimingFunction: "ease-out",
                    ...defaultUnderlineStyle,
                    fontWeight: "bold",
                    textDecoration: "none",
                    _hover: {
                        color: "gray.500",
                        textDecorationColor: "gray.500",
                    },
                    // We need this because the way of how Contentful hanldes rich text.
                    "& span": {
                        fontWeight: "bold",
                    },
                    _focusVisible: {
                        outline: "2px solid",
                        outlineColor: "gray.500",
                        outlineOffset: "2px",
                        textDecoration: "none",
                    },
                }}
            >
                {isEmpty(children) ? label : children}
            </Text>
        );
    }
);

ModalLink.displayName = "ModalLink";
